import PropTypes from 'prop-types'
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { components } from 'react-select'

// Space kept between a shifted menu and the window edge.
const VIEWPORT_GUTTER = 8

// react-select opens the menu from the control's left edge. A table filter's menu is portaled to
// the body and is wider than its column, so near the right edge it would run past the window and
// cut off the counts. Shift it left just enough to stay inside the window (never past its left edge).
function Menu(props) {
  const { innerRef, innerProps, selectProps } = props
  const menuRef = useRef(null)
  const shiftRef = useRef(0)
  const [shift, setShift] = useState(0)

  const setRefs = useCallback(
    (node) => {
      menuRef.current = node
      innerRef?.(node)
    },
    [innerRef],
  )

  // Measure on open and re-measure when the typed text changes: typing filters the options, which can
  // change the menu's width. It must not re-run on its own result (or on every render): the measured
  // viewport width can change with the shift (for example a classic scrollbar appearing), which would
  // flip the shift back and forth forever and crash the page ("Maximum update depth exceeded").
  const inputValue = selectProps?.inputValue
  useLayoutEffect(() => {
    if (!menuRef.current) {
      return
    }
    // Measure where the menu would sit without the current shift.
    const rect = menuRef.current.getBoundingClientRect()
    const left = rect.left - shiftRef.current
    const right = rect.right - shiftRef.current
    const overflow = right - (document.documentElement.clientWidth - VIEWPORT_GUTTER)
    const nextShift = overflow > 0 ? Math.max(-overflow, Math.min(0, VIEWPORT_GUTTER - left)) : 0
    shiftRef.current = nextShift
    setShift(nextShift)
  }, [inputValue])

  // The offset is measured in pixels, so it can only be applied inline.
  const shiftedInnerProps = useMemo(
    () => (shift ? { ...innerProps, style: { transform: `translateX(${shift}px)` } } : innerProps),
    [innerProps, shift],
  )

  return <components.Menu {...props} innerRef={setRefs} innerProps={shiftedInnerProps} />
}

Menu.propTypes = {
  selectProps: PropTypes.shape({ inputValue: PropTypes.string }),
  // react-select's own callback ref (it measures the menu for placement).
  innerRef: PropTypes.func,
  innerProps: PropTypes.shape({ onMouseDown: PropTypes.func, onMouseMove: PropTypes.func }),
}

export default Menu
